import React, { useState, useCallback } from "react";
import {
  loadTypographyFromStorage,
  saveTypographyToStorage,
  DEFAULT_TYPOGRAPHY,
  buildBodyStyle,
  buildSectionTitleStyle,
  buildDateStyle,
  getDateAsHtml,
  getSectionAsHtml,
  FONT_FAMILIES,
  TEXT_TRANSFORMS,
} from "./WorkFormatTypography";
import { convertUnicodeToLegacy, TAMIL_ENCODINGS } from "./utils/tamil_converter";


const PAD_LEN = 25;

const formatTime = (t, showMidnight = true) => {
  const isEmpty = t === undefined || t === null || t === "";
  if (isEmpty || t === "00:00") return showMidnight ? "00:00" : "";
  return t;
};

const formatMuhurta = (muhurta, showMidnight) => {
  if (!muhurta || typeof muhurta !== "object") return [];
  const entries = Object.entries(muhurta).reduce((acc, [key, value]) => {
    const idx = key.match(/\d+/)?.[0];
    if (!idx) return acc;
    if (!acc[idx]) acc[idx] = {};
    acc[idx][key.includes("start") ? "start" : "end"] = value;
    return acc;
  }, {});
  return Object.entries(entries)
    .sort((a, b) => Number(a[0]) - Number(b[0]))
    .map(([idx, { start, end }]) => `${formatTime(start, showMidnight)} - ${end || "24:00"}`);
};

const SPACING_PRESETS = [
  { id: "1tab", label: "1 tab", tabs: 1, spaces: 0, fixedWidth: false },
  { id: "2tabs", label: "2 tabs", tabs: 2, spaces: 0, fixedWidth: false },
  { id: "3tabs", label: "3 tabs", tabs: 3, spaces: 0, fixedWidth: false },
  { id: "fixed", label: "Spaces (aligned, 25)", tabs: 0, spaces: 0, fixedWidth: true },
  { id: "1tab2sp", label: "1 tab + 2 spaces", tabs: 1, spaces: 2, fixedWidth: false },
  { id: "2tabs1sp", label: "2 tabs + 1 space", tabs: 2, spaces: 1, fixedWidth: false },
  { id: "custom", label: "Custom…", tabs: 1, spaces: 0, fixedWidth: false },
];

const getSeparatorString = (preset, customTabs, customSpaces) => {
  const t = preset === "custom" ? customTabs : (SPACING_PRESETS.find((p) => p.id === preset)?.tabs ?? 0);
  const s = preset === "custom" ? customSpaces : (SPACING_PRESETS.find((p) => p.id === preset)?.spaces ?? 0);
  const tabs = Math.max(0, parseInt(t, 10) || 0);
  const spaces = Math.max(0, parseInt(s, 10) || 0);
  const sep = "\t".repeat(tabs) + " ".repeat(spaces);
  return sep || " "; // never return empty so label and time don't run together
};

/** If the label accidentally includes the time (e.g. "05. Gara 00:47"), strip it so spacing aligns. */
const stripTrailingTimeFromLabel = (label) => {
  const s = String(label);
  const m = s.match(/\s+\d{1,2}:\d{2}$/);
  return m ? s.slice(0, -m[0].length) : s;
};

const buildLine = (label, time, spacingConfig, showMidnight) => {
  const cleanLabel = stripTrailingTimeFromLabel(label);
  const { preset, customTabs, customSpaces } = spacingConfig;
  const p = SPACING_PRESETS.find((x) => x.id === preset);
  if (p?.fixedWidth) {
    // padEnd only adds spaces; never truncates, so long labels are preserved
    return `${cleanLabel.padEnd(PAD_LEN, " ")}${formatTime(time, showMidnight)}`;
  }
  const sep = getSeparatorString(preset, customTabs, customSpaces);
  return `${cleanLabel}${sep}${formatTime(time, showMidnight)}`;
};

const getSectionText = (entry, sectionKey, spacingConfig, showMidnight, encoding = "Unicode") => {
  const conv = (txt) => convertUnicodeToLegacy(txt, encoding);
  const line = (label, time) => buildLine(conv(label), time, spacingConfig, showMidnight);
  switch (sectionKey) {
    case "header":
      return [
        `${conv(entry.date)} — ${conv(entry.weekday)}`,
        `Rahukalam: ${entry.rahukalam || "—"}`,
        `Yamagandam: ${entry.yamagandam || "—"}`,
      ].join("\n");
    case "panchang":
      return [
        ...(entry.nakshatra || []).map(([name, t]) => line(name, t)),
        ...(entry.tithi || []).map(([name, t]) => line(name, t)),
        ...(entry.yoga || []).map(([name, t]) => line(name, t)),
        ...(entry.karana || []).map(([name, t]) => line(name, t)),
      ].join("\n");
    case "nakshatra":
      return (entry.nakshatra || []).map(([name, t]) => line(name, t)).join("\n");
    case "tithi":
      return (entry.tithi || []).map(([name, t]) => line(name, t)).join("\n");
    case "yoga":
      return (entry.yoga || []).map(([name, t]) => line(name, t)).join("\n");
    case "karana":
      return (entry.karana || []).map(([name, t]) => line(name, t)).join("\n");
    case "rashi":
      return (entry.rashi || []).map(([name, t]) => line(name, t)).join("\n");
    case "muhurta": {
      const arr = formatMuhurta(entry.muhurta, showMidnight);
      return arr.length ? arr.join("\n") : "No favourable timings";
    }
    case "activities":
      return (entry.activities || []).map((a) => {
        const text = typeof a === "string" ? a : String(a?.join?.(" ") ?? a);
        return conv(text);
      }).join("\n\n");
    case "inauspicious":
      return conv(entry.inauspicious) || "";
    default:
      return "";
  }
};


const getDateAsPlainText = (entry, spacingConfig, showMidnight, encoding = "Unicode") => {
  const sections = [
    ["header", "Header"],
    ["panchang", "Panchang"],
    ["rashi", "Rashi"],
    ["muhurta", "Favorable timings"],
    ["activities", "Activities"],
    ["inauspicious", "Inauspicious"],
  ];
  const parts = [];
  for (const [key, title] of sections) {
    const text = getSectionText(entry, key, spacingConfig, showMidnight, encoding);
    if (text) parts.push(`${title}\n${text}`);
  }
  return parts.join("\n\n");
};


const WorkFormat = ({ panchangData, showMidnight = true }) => {
  const [copiedFull, setCopiedFull] = useState(null);
  const [copiedSection, setCopiedSection] = useState(null);
  const [typographyPanelOpen, setTypographyPanelOpen] = useState(false);
  const [typography, setTypographyState] = useState(loadTypographyFromStorage);
  const setTypography = useCallback((updates) => {
    setTypographyState((prev) => {
      const next = { ...prev, ...updates };
      saveTypographyToStorage(next);
      return next;
    });
  }, []);

  const [spacingPreset, setSpacingPreset] = useState(() => {
    try {
      return localStorage.getItem("work-format-spacing-preset") || "fixed";
    } catch {
      return "fixed";
    }
  });
  const [customTabs, setCustomTabs] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("work-format-custom-tabs"), 10);
      return Number.isFinite(v) && v >= 0 ? v : 1;
    } catch {
      return 1;
    }
  });
  const [customSpaces, setCustomSpaces] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem("work-format-custom-spaces"), 10);
      return Number.isFinite(v) && v >= 0 ? v : 0;
    } catch {
      return 0;
    }
  });

  const spacingConfig = { preset: spacingPreset, customTabs, customSpaces };

  const persistSpacing = useCallback((preset, tabs, spaces) => {
    try {
      localStorage.setItem("work-format-spacing-preset", preset);
      localStorage.setItem("work-format-custom-tabs", String(tabs));
      localStorage.setItem("work-format-custom-spaces", String(spaces));
    } catch (_) {}
  }, []);

  const copyFull = (entry, index) => {
    const plain = getDateAsPlainText(entry, spacingConfig, showMidnight, typography.encoding);
    if (typography.copyFormat === "html") {

      const html = getDateAsHtml(entry, spacingConfig, typography, buildLine, formatMuhurta, showMidnight);
      navigator.clipboard.write([
        new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([plain], { type: "text/plain" }) }),
      ]).then(() => {
        setCopiedFull(index);
        setCopiedSection(null);
        setTimeout(() => setCopiedFull(null), 2000);
      }).catch(() => {
        navigator.clipboard.writeText(plain).then(() => {
          setCopiedFull(index);
          setCopiedSection(null);
          setTimeout(() => setCopiedFull(null), 2000);
        });
      });
    } else {
      navigator.clipboard.writeText(plain).then(() => {
        setCopiedFull(index);
        setCopiedSection(null);
        setTimeout(() => setCopiedFull(null), 2000);
      });
    }
  };

  const copySection = (entry, index, sectionKey) => {
    const text = getSectionText(entry, sectionKey, spacingConfig, showMidnight, typography.encoding);
    if (!text) return;

    if (typography.copyFormat === "html") {
      const html = getSectionAsHtml(entry, sectionKey, spacingConfig, typography, buildLine, formatMuhurta, showMidnight);
      if (html) {
        navigator.clipboard.write([
          new ClipboardItem({ "text/html": new Blob([html], { type: "text/html" }), "text/plain": new Blob([text], { type: "text/plain" }) }),
        ]).then(() => {
          setCopiedSection(`${index}-${sectionKey}`);
          setCopiedFull(null);
          setTimeout(() => setCopiedSection(null), 2000);
        }).catch(() => {
          navigator.clipboard.writeText(text).then(() => {
            setCopiedSection(`${index}-${sectionKey}`);
            setCopiedFull(null);
            setTimeout(() => setCopiedSection(null), 2000);
          });
        });
        return;
      }
    }
    navigator.clipboard.writeText(text).then(() => {
      setCopiedSection(`${index}-${sectionKey}`);
      setCopiedFull(null);
      setTimeout(() => setCopiedSection(null), 2000);
    });
  };

  const bodyStyle = buildBodyStyle(typography);
  const sectionTitleStyle = buildSectionTitleStyle(typography);
  const dateStyle = buildDateStyle(typography);

  return (
    <div className="work-format-list">
      <div className="work-format-spacing-toolbar">
        <label htmlFor="work-format-spacing-preset" className="work-format-spacing-label">
          Copy spacing (component → timing):
        </label>
        <select
          id="work-format-spacing-preset"
          className="form-select work-format-spacing-select"
          value={spacingPreset}
          onChange={(e) => {
            const v = e.target.value;
            setSpacingPreset(v);
            persistSpacing(v, customTabs, customSpaces);
          }}
          title="Choose how many tabs/spaces go between panchang names and timings when copying (e.g. for InDesign)"
        >
          {SPACING_PRESETS.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        {spacingPreset === "custom" && (
          <>
            <label htmlFor="work-format-custom-tabs" className="work-format-spacing-sublabel">Tabs</label>
            <input
              id="work-format-custom-tabs"
              type="number"
              min={0}
              max={8}
              className="form-control work-format-spacing-input"
              value={customTabs}
              onChange={(e) => {
                const v = Math.max(0, Math.min(8, parseInt(e.target.value, 10) || 0));
                setCustomTabs(v);
                persistSpacing(spacingPreset, v, customSpaces);
              }}
            />
            <label htmlFor="work-format-custom-spaces" className="work-format-spacing-sublabel">Spaces</label>
            <input
              id="work-format-custom-spaces"
              type="number"
              min={0}
              max={20}
              className="form-control work-format-spacing-input"
              value={customSpaces}
              onChange={(e) => {
                const v = Math.max(0, Math.min(20, parseInt(e.target.value, 10) || 0));
                setCustomSpaces(v);
                persistSpacing(spacingPreset, customTabs, v);
              }}
            />
          </>
        )}
      </div>
      <div className="work-format-typography-panel">
        <button
          type="button"
          className="work-format-typography-toggle"
          onClick={() => setTypographyPanelOpen((o) => !o)}
          aria-expanded={typographyPanelOpen}
        >
          {typographyPanelOpen ? "▼" : "▶"} Typography & format (font size, spacing, copy with formatting)
        </button>
        {typographyPanelOpen && (
          <div className="work-format-typography-grid">
            {typography.encoding !== "Unicode" && (
              <div className="legacy-encoding-warning">
                <strong>Legacy Encoding Notice:</strong> You have selected <b>{typography.encoding}</b>.
                The text below will look scrambled (ÌÍÎ) on the web. This is normal.
                To see it correctly, you must:
                <ol>
                  <li>Select a <b>{typography.encoding}</b> font from the list below (e.g., <i>TAM-Kalyani</i>).</li>
                  <li>Ensure that font is <b>installed on your computer</b>.</li>
                  <li>The "Copy" function will now work correctly for your legacy software.</li>
                </ol>
              </div>
            )}
            <div className="work-format-typography-row">

              <label className="work-format-typo-label">Font family</label>
              <select className="form-select work-format-typo-select" value={typography.fontFamily} onChange={(e) => setTypography({ fontFamily: e.target.value })}>
                {FONT_FAMILIES.map((f) => <option key={f} value={f}>{f}</option>)}
              </select>
              <label className="work-format-typo-label">Font size (pt)</label>
              <input type="number" step="0.01" min="6" max="72" className="form-control work-format-typo-num" value={typography.fontSizePt} onChange={(e) => setTypography({ fontSizePt: Math.max(6, Math.min(72, parseFloat(e.target.value) || 10)) })} title="Body font size, 2 decimals" />
              <label className="work-format-typo-label">Weight</label>
              <select className="form-select work-format-typo-select" value={typography.fontWeight} onChange={(e) => setTypography({ fontWeight: e.target.value })}>
                <option value="normal">Normal</option>
                <option value="bold">Bold</option>
              </select>
              <label className="work-format-typo-label">Style</label>
              <select className="form-select work-format-typo-select" value={typography.fontStyle} onChange={(e) => setTypography({ fontStyle: e.target.value })}>
                <option value="normal">Normal</option>
                <option value="italic">Italic</option>
              </select>
            </div>
            <div className="work-format-typography-row">
              <label className="work-format-typo-label">Letter spacing (pt)</label>
              <input type="number" step="0.01" min="-5" max="20" className="form-control work-format-typo-num" value={typography.letterSpacingPt} onChange={(e) => setTypography({ letterSpacingPt: parseFloat(e.target.value) || 0 })} />
              <label className="work-format-typo-label">Word spacing (pt)</label>
              <input type="number" step="0.01" min="-2" max="20" className="form-control work-format-typo-num" value={typography.wordSpacingPt} onChange={(e) => setTypography({ wordSpacingPt: parseFloat(e.target.value) || 0 })} />
              <label className="work-format-typo-label">Line height</label>
              <input type="number" step="0.01" min="0.8" max="3" className="form-control work-format-typo-num" value={typography.lineHeight} onChange={(e) => setTypography({ lineHeight: Math.max(0.8, Math.min(3, parseFloat(e.target.value) || 1.4)) })} />
              <label className="work-format-typo-label">Transform</label>
              <select className="form-select work-format-typo-select" value={typography.textTransform} onChange={(e) => setTypography({ textTransform: e.target.value })}>
                {TEXT_TRANSFORMS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div className="work-format-typography-row">
              <label className="work-format-typo-label">Underline</label>
              <input type="checkbox" className="form-check-input work-format-typo-check" checked={typography.underline} onChange={(e) => setTypography({ underline: e.target.checked })} />
              <label className="work-format-typo-label">Strikethrough</label>
              <input type="checkbox" className="form-check-input work-format-typo-check" checked={typography.strikethrough} onChange={(e) => setTypography({ strikethrough: e.target.checked })} />
              <label className="work-format-typo-label">Text color</label>
              <input type="color" className="form-control form-control-color work-format-typo-color" value={typography.color} onChange={(e) => setTypography({ color: e.target.value })} />
              <label className="work-format-typo-label">Background</label>
              <input type="color" className="form-control form-control-color work-format-typo-color" value={typography.backgroundColor === "transparent" ? "#ffffff" : typography.backgroundColor} onChange={(e) => setTypography({ backgroundColor: e.target.value })} />
            </div>
            <div className="work-format-typography-row">
              <label className="work-format-typo-label">Margin before (pt)</label>
              <input type="number" step="0.5" min="0" max="24" className="form-control work-format-typo-num" value={typography.marginBeforePt} onChange={(e) => setTypography({ marginBeforePt: Math.max(0, parseFloat(e.target.value) || 0) })} />
              <label className="work-format-typo-label">Margin after (pt)</label>
              <input type="number" step="0.5" min="0" max="24" className="form-control work-format-typo-num" value={typography.marginAfterPt} onChange={(e) => setTypography({ marginAfterPt: Math.max(0, parseFloat(e.target.value) || 4) })} />
              <label className="work-format-typo-label">Section title size (pt)</label>
              <input type="number" step="0.01" min="6" max="72" className="form-control work-format-typo-num" value={typography.sectionTitleFontSizePt} onChange={(e) => setTypography({ sectionTitleFontSizePt: Math.max(6, Math.min(72, parseFloat(e.target.value) || 11)) })} />
              <label className="work-format-typo-label">Section bold</label>
              <input type="checkbox" className="form-check-input work-format-typo-check" checked={typography.sectionTitleBold} onChange={(e) => setTypography({ sectionTitleBold: e.target.checked })} />
            </div>
            <div className="work-format-typography-row">
              <label className="work-format-typo-label">Date size (pt)</label>
              <input type="number" step="0.01" min="6" max="72" className="form-control work-format-typo-num" value={typography.dateFontSizePt} onChange={(e) => setTypography({ dateFontSizePt: Math.max(6, Math.min(72, parseFloat(e.target.value) || 12)) })} />
              <label className="work-format-typo-label">Date bold</label>
              <input type="checkbox" className="form-check-input work-format-typo-check" checked={typography.dateBold} onChange={(e) => setTypography({ dateBold: e.target.checked })} />
              <label className="work-format-typo-label">Timing size (pt)</label>
              <input type="number" step="0.01" min="6" max="72" className="form-control work-format-typo-num" value={typography.timingFontSizePt} onChange={(e) => setTypography({ timingFontSizePt: Math.max(6, Math.min(72, parseFloat(e.target.value) || 10)) })} />
              <label className="work-format-typo-label">Scale X (%)</label>
              <input type="number" step="1" min="50" max="200" className="form-control work-format-typo-num" value={typography.horizontalScalePct} onChange={(e) => setTypography({ horizontalScalePct: Math.max(50, Math.min(200, parseInt(e.target.value, 10) || 100)) })} />
            </div>
            <div className="work-format-typography-row">
              <label className="work-format-typo-label">Baseline shift (pt)</label>
              <input type="number" step="0.5" min="-20" max="20" className="form-control work-format-typo-num" value={typography.baselineShiftPt} onChange={(e) => setTypography({ baselineShiftPt: Math.max(-20, Math.min(20, parseFloat(e.target.value) || 0)) })} />
              <label className="work-format-typo-label">Superscript</label>
              <input type="checkbox" className="form-check-input work-format-typo-check" checked={typography.superscript} onChange={(e) => setTypography({ superscript: e.target.checked, subscript: e.target.checked ? false : typography.subscript })} />
              <label className="work-format-typo-label">Subscript</label>
              <input type="checkbox" className="form-check-input work-format-typo-check" checked={typography.subscript} onChange={(e) => setTypography({ subscript: e.target.checked, superscript: e.target.checked ? false : typography.superscript })} />
              <label className="work-format-typo-label">Copy as</label>
              <select className="form-select work-format-typo-select" value={typography.copyFormat} onChange={(e) => setTypography({ copyFormat: e.target.value })} title="HTML keeps font/size/color in Word/InDesign">
                <option value="html">With formatting (HTML)</option>
                <option value="plain">Plain text only</option>
              </select>
              <label className="work-format-typo-label">Tamil Encoding</label>
              <select className="form-select work-format-typo-select" value={typography.encoding} onChange={(e) => setTypography({ encoding: e.target.value })} title="Choose legacy encoding (TAM/TAB/TSCII) if you get unintelligible characters when pasting into InDesign/Word">
                {TAMIL_ENCODINGS.map((enc) => (
                  <option key={enc} value={enc}>{enc}</option>
                ))}
              </select>
            </div>

            <div className="work-format-typography-row work-format-typography-reset">
              <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => { setTypographyState(DEFAULT_TYPOGRAPHY); saveTypographyToStorage(DEFAULT_TYPOGRAPHY); }}>Reset to defaults</button>
            </div>
          </div>
        )}
      </div>
      {panchangData.map((entry, index) => {
        const muhurtaArr = formatMuhurta(entry.muhurta, showMidnight);
        const hasActivities = entry.activities && entry.activities.length > 0;
        const hasInauspicious = !!entry.inauspicious;

        return (
          <div key={index} className="work-format-entry">
            <div className="work-format-entry-head">
              <span className="work-format-date" style={dateStyle}>{entry.date}</span>
              <span className="work-format-weekday" style={dateStyle}>{entry.weekday}</span>
              <button
                type="button"
                className="work-format-copy-btn"
                onClick={() => copyFull(entry, index)}
                title="Copy full day"
                aria-label="Copy full"
              >
                <span className="work-format-copy-icon">
                  {copiedFull === index ? "✓" : "⎘"}
                </span>
              </button>
            </div>

            <div className="work-format-zones">
              <div className="work-format-zone">
                <button
                  type="button"
                  className="work-format-zone-copy"
                  onClick={() => copySection(entry, index, "header")}
                  title="Copy header"
                  aria-label="Copy header"
                >
                  {copiedSection === `${index}-header` ? "✓" : "⎘"}
                </button>
                <div className="work-format-panchang-lines" style={bodyStyle}>
                  <span className="work-line">Rahukalam: {entry.rahukalam || "—"}</span>
                  <span className="work-line">Yamagandam: {entry.yamagandam || "—"}</span>
                </div>
              </div>

              <div className="work-format-zone">
                <button
                  type="button"
                  className="work-format-zone-copy"
                  onClick={() => copySection(entry, index, "panchang")}
                  title="Copy Panchang (Nakshatra, Tithi, Yoga, Karana)"
                  aria-label="Copy panchang"
                >
                  {copiedSection === `${index}-panchang` ? "✓" : "⎘"}
                </button>
                <pre className="work-format-panchang-lines work-format-panchang-single" style={bodyStyle}>
                  {(entry.nakshatra || []).map(([name, t], i) => (
                    <span key={`n-${i}`} className="work-line">{buildLine(convertUnicodeToLegacy(name, typography.encoding), t, spacingConfig, showMidnight)}</span>
                  ))}
                  {(entry.tithi || []).map(([name, t], i) => (
                    <span key={`t-${i}`} className="work-line">{buildLine(convertUnicodeToLegacy(name, typography.encoding), t, spacingConfig, showMidnight)}</span>
                  ))}
                  {(entry.yoga || []).map(([name, t], i) => (
                    <span key={`y-${i}`} className="work-line">{buildLine(convertUnicodeToLegacy(name, typography.encoding), t, spacingConfig, showMidnight)}</span>
                  ))}
                  {(entry.karana || []).map(([name, t], i) => (
                    <span key={`k-${i}`} className="work-line">{buildLine(convertUnicodeToLegacy(name, typography.encoding), t, spacingConfig, showMidnight)}</span>
                  ))}
                </pre>

              </div>

              {(entry.rashi || []).length > 0 && (
                <div className="work-format-zone">
                  <button
                    type="button"
                    className="work-format-zone-copy"
                    onClick={() => copySection(entry, index, "rashi")}
                    title="Copy Rashi"
                    aria-label="Copy"
                  >
                    {copiedSection === `${index}-rashi` ? "✓" : "⎘"}
                  </button>
                  <pre className="work-format-panchang-lines" style={bodyStyle}>
                    {(entry.rashi || []).map(([name, t], i) => (
                      <span key={i} className="work-line">{buildLine(convertUnicodeToLegacy(name, typography.encoding), t, spacingConfig, showMidnight)}</span>
                    ))}
                  </pre>

                </div>
              )}

              {muhurtaArr.length > 0 ? (
                <div className="work-format-zone">
                  <button
                    type="button"
                    className="work-format-zone-copy"
                    onClick={() => copySection(entry, index, "muhurta")}
                    title="Copy Favorable timings"
                    aria-label="Copy"
                  >
                    {copiedSection === `${index}-muhurta` ? "✓" : "⎘"}
                  </button>
                  <div className="work-format-panchang-lines" style={bodyStyle}>
                    <strong style={sectionTitleStyle}>Favorable timings:</strong>
                    {muhurtaArr.map((s, i) => (
                      <span key={i} className="work-line">{s}</span>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="work-format-zone">
                  <div className="work-format-panchang-lines work-format-no-favorable" style={bodyStyle}>
                    No favourable timings
                  </div>
                </div>
              )}

              {hasActivities && (
                <div className="work-format-zone">
                  <button
                    type="button"
                    className="work-format-zone-copy"
                    onClick={() => copySection(entry, index, "activities")}
                    title="Copy Activities"
                    aria-label="Copy"
                  >
                    {copiedSection === `${index}-activities` ? "✓" : "⎘"}
                  </button>
                  <div className="work-format-activity work-format-panchang-lines" style={bodyStyle}>
                    {entry.activities.map((act, i) => {
                      const text = typeof act === "string" ? act : String(act?.join?.(" ") ?? act);
                      return (
                        <span key={i} className="work-line" style={{ whiteSpace: "pre-wrap" }}>
                          {convertUnicodeToLegacy(text, typography.encoding)}
                        </span>
                      );
                    })}
                  </div>

                </div>
              )}

              {hasInauspicious && (
                <div className="work-format-zone">
                  <button
                    type="button"
                    className="work-format-zone-copy"
                    onClick={() => copySection(entry, index, "inauspicious")}
                    title="Copy Inauspicious"
                    aria-label="Copy"
                  >
                    {copiedSection === `${index}-inauspicious` ? "✓" : "⎘"}
                  </button>
                  <div className="work-format-inauspicious work-format-panchang-lines" style={bodyStyle}>
                    {convertUnicodeToLegacy(entry.inauspicious, typography.encoding)}
                  </div>

                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default WorkFormat;
